import { type GroupChannel } from "@sendbird/chat/groupChannel";
import { type FileMessageCreateParams } from "@sendbird/chat/message";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

interface SendFileMessageParams {
  channel: GroupChannel;
  file: File;
}

interface FileUploadProgressInfo {
  loaded: number;
  total: number;
  percentage: number;
}

/**
 * Mutation hook to send a file message to a Sendbird channel with upload progress tracking.
 * Automatically attaches workplace user metadata to the message.
 *
 * @example
 * ```tsx
 * const { mutate: sendFile, isPending, uploadProgress } = useSendFileMessage();
 *
 * sendFile({
 *   channel,
 *   file: selectedFile,
 * });
 * ```
 */
export function useSendFileMessage() {
  const queryClient = useQueryClient();
  const session = useSession();
  const [uploadProgress, setUploadProgress] = useState<FileUploadProgressInfo | null>(null);

  const mutation = useMutation({
    mutationFn: async ({ channel, file }: SendFileMessageParams) => {
      setUploadProgress({ loaded: 0, total: file.size, percentage: 0 });

      const params: FileMessageCreateParams = {
        file,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        data: JSON.stringify({
          workplaceUserId: session.user?._id,
          workplaceUserName: session.user?.name,
        }),
      };

      // Wrap MessageRequestHandler in a promise
      return await new Promise((resolve, reject) => {
        const handler = channel.sendFileMessage(params);
        handler
          .onSucceeded((message) => {
            resolve(message);
          })
          .onFailed((error) => {
            reject(error);
          });
      });
    },
    onSuccess: (_, variables) => {
      // Reset progress
      setUploadProgress(null);

      // Invalidate messages query to trigger refetch
      void queryClient.invalidateQueries({
        queryKey: ["chat", "messages", variables.channel.url],
      });
    },
    onError: () => {
      // Reset progress on error
      setUploadProgress(null);
    },
    meta: {
      userErrorMessage: "Failed to upload file",
    },
  });

  return {
    ...mutation,
    uploadProgress,
  };
}
